
import { pb } from '@/lib/pocketbase';

interface AdminNotificationData {
  type: 'contact_form' | 'new_tutor' | 'new_student' | 'new_booking' | 'subject_request' | 'new_lesson' | 'new_message' | 'live_chat' | 'profile_suspension_request';
  title: string;
  message: string;
  entityId: string;
  entityType: string;
  data?: any;
}

export const adminNotificationService = {
  // Send notification to all admins
  sendToAllAdmins: async (notificationData: AdminNotificationData) => {
    try {
      // Get all admin users
      const admins = await pb.collection('users').getList(1, 100, {
        filter: 'role = "admin"'
      });
      
      if (admins.items.length === 0) {
        return;
      }

      // Send notification to each admin
      const notificationPromises = admins.items.map(async admin => {
        const notificationRecord = {
          userId: admin.id,
          type: 'admin_notification', // Use single type for all admin notifications
          title: notificationData.title,
          message: notificationData.message,
          isRead: false,
          createdAt: new Date().toISOString(),
          data: {
            ...notificationData.data,
            originalType: notificationData.type, // Store original type in data
            entityId: notificationData.entityId,
            entityType: notificationData.entityType
          }
        };
        
        try {
          const result = await pb.collection('notifications').create(notificationRecord);
          return result;
        } catch (createError) {
          throw createError;
        }
      });

      const results = await Promise.all(notificationPromises);
      return results;
    } catch (error) {
      // Don't throw error to prevent breaking the main functionality
    }
  },

  // Specific notification functions
  notifyNewContactForm: async (sessionId: string, visitorName: string, subject: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'contact_form',
      title: 'New Contact Form',
      message: `${visitorName} submitted a contact form: "${subject}"`,
      entityId: sessionId,
      entityType: 'contact_form',
      data: { visitorName, subject }
    });
  },

  notifyNewTutor: async (tutorId: string, tutorName: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'new_tutor',
      title: 'New Tutor Registration',
      message: `${tutorName} has registered as a tutor and needs approval`,
      entityId: tutorId,
      entityType: 'tutor',
      data: { tutorName }
    });
  },

  notifyNewStudent: async (studentId: string, studentName: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'new_student',
      title: 'New Student Registration',
      message: `${studentName} has registered as a student`,
      entityId: studentId,
      entityType: 'student',
      data: { studentName }
    });
  },

  notifyNewBooking: async (bookingId: string, studentName: string, tutorName: string, subject: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'new_booking',
      title: 'New Booking',
      message: `${studentName} booked a ${subject} session with ${tutorName}`,
      entityId: bookingId,
      entityType: 'booking',
      data: { studentName, tutorName, subject }
    });
  },

  notifyNewSubjectRequest: async (requestId: string, tutorName: string, subjectName: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'subject_request',
      title: 'New Subject Request',
      message: `${tutorName} requested to add "${subjectName}" as a subject`,
      entityId: requestId,
      entityType: 'subject_request',
      data: { tutorName, subjectName }
    });
  },

  notifyNewLesson: async (lessonId: string, tutorName: string, lessonTitle: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'new_lesson',
      title: 'New Lesson Created',
      message: `${tutorName} created a new lesson: "${lessonTitle}"`,
      entityId: lessonId,
      entityType: 'lesson',
      data: { tutorName, lessonTitle }
    });
  },

  notifyNewMessage: async (senderId: string, senderName: string, receiverName: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'new_message',
      title: 'New Message',
      message: `${senderName} sent a message to ${receiverName}`,
      entityId: senderId,
      entityType: 'message',
      data: { senderName, receiverName }
    });
  },

  notifyProfileSuspensionRequest: async (requestId: string, userName: string, userEmail: string, requestType: 'suspend' | 'delete', reason: string) => {
    await adminNotificationService.sendToAllAdmins({
      type: 'profile_suspension_request',
      title: `Profile ${requestType === 'suspend' ? 'Suspension' : 'Deletion'} Request`,
      message: `${userName || userEmail} has requested to ${requestType} their profile. Reason: ${reason}`,
      entityId: requestId,
      entityType: 'profile_suspension_request',
      data: { userName, userEmail, requestType, reason }
    });
  }
};
